/**
 * space/test-api.js — the `window.__cc.space` surface.
 *
 * Kept apart from the module it exposes for one reason: tests are the only
 * consumer, and mixing "what the game does" with "how a test drives it" makes
 * both harder to read. `core/testmode.js` reaches this through
 * `wired.space.testApi`, so the shape here can grow without touching a file
 * eight other agents are also appending to.
 *
 * Every entry either reads state or calls the same function a real click would.
 * Nothing here is a test-only code path through the game itself.
 *
 * @module space/test-api
 */

import { ROOM_THEMES, SHELF_THEMES, SPACE_MUSIC } from '../constants.js';
import { save, economy } from '../core/save.js';
import { pointer, menuHost } from './pointer.js';
import { tankTier } from './fish-tank.js';
import * as shop from './shop.js';

/**
 * @param {object} api The space module's own exports plus its private handles.
 * @returns {object} The object installed as `window.__cc.space`.
 */
export function makeTestApi(api) {
  const { get, naming, ctx } = api;

  /** The instance the player is looking at, falling back to their own. */
  const view = () => get.current() || get.own();

  return {
    enter: api.enter,
    exit: api.exit,
    isInside: api.isInside,
    visit: api.visit,
    serialize: api.serialize,
    renderVisit: api.renderVisit,
    endVisit: api.endVisit,
    nameFish: api.nameFish,
    shelves: api.shelves,
    tank: api.tank,
    guestbook: api.guestbook,

    /** @returns {object} Everything a test wants about the space at once. */
    state() {
      const s = save.data.space;
      const inst = view();
      const current = get.current();
      const own = get.own();
      return {
        inside: api.isInside(),
        owner: inst ? (inst.model?.owner ?? null) : null,
        visiting: current && current !== own ? (current.model?.owner ?? null) : null,
        readOnly: current ? current.readOnly : false,
        shelfTheme: s.theme,
        roomTheme: s.roomTheme,
        music: s.music,
        shelfSlots: s.shelfSlots,
        tankCapacity: s.tankCapacity,
        tankTier: tankTier(s.tankCapacity).id,
        stars: economy.stars(),
        plush: inst ? inst.shelves.list() : [],
        fish: inst ? inst.tank.list() : [],
        waitlist: inst ? inst.tank.waitlist() : [],
        decor: inst ? inst.decor.list() : [],
        guestbook: inst ? inst.guestbook.list() : [],
        arrangement: own ? own.shelves.arrangement() : {},
        pendingNames: naming() ? naming().pending() : [],
        namingOpen: naming() ? naming().isOpen() : false,
        namingChoices: naming() ? naming().suggestions() : [],
        held: own ? own.shelves.heldId : null,
        hovered: pointer.hoveredId(),
        menu: menuHost.openKey(),
        focusedFish: inst ? inst.tank.focusedId() : null,
        cardFor: inst ? inst.tank.cardFor() : null,
        avatar: ctx.avatar ? ctx.avatar.getPosition().toArray() : null,
        npcId: api.NPC_ID,
      };
    },

    decor: {
      anchors: () => (get.own() ? get.own().decor.anchorIds() : []),
      list: () => (get.own() ? get.own().decor.list() : save.data.space.decor.slice()),
      place: shop.placeDecor,
      remove: shop.removeDecor,
      openPicker: (anchorId) => (get.own() ? get.own().decor.openPicker(anchorId) : []),
      setRoomTheme: shop.setRoomTheme,
      setMusic: shop.setMusic,
      themes: () => ROOM_THEMES.map((t) => ({ ...t, owned: shop.owns(t.id) })),
      shelfThemes: () => SHELF_THEMES.map((t) => ({ ...t, owned: shop.owns(t.id) })),
      music: () => SPACE_MUSIC.map((m) => ({ ...m })),
    },

    naming: {
      pending: () => naming().pending(),
      open: (fishId) => naming().open(fishId),
      suggestions: () => naming().suggestions(),
      submit: (name) => naming().submit(name),
      random: () => naming().random(),
      keep: () => naming().keep(),
      dismiss: () => naming().dismiss(),
      isOpen: () => naming().isOpen(),
    },

    /** Fire an in-world item's handler the way a real pointer click would. */
    click: (id) => pointer.selectById(id),
    /** @returns {string[]} Every pickable id currently registered. */
    pickables: () => [...pointer.items.keys()],
    /** @returns {string|null} */
    openMenu: () => menuHost.openKey(),
    /** Deterministic swim sampling — see space/fish-swim.js. */
    sample: (fishId, t) => (view()?.tank.sample(fishId, t) ?? null),
    buyShelfExpansion: shop.buyShelfExpansion,
    setShelfTheme: shop.setShelfTheme,
    upgradeTank: shop.upgradeTank,
  };
}
